import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Terminal, Boxes, Eye, GitBranch, Sparkles, Zap } from "lucide-react";

const providers = [
  { name: "Claude", sub: "Anthropic", color: "#D97757" },
  { name: "OpenAI", sub: "GPT", color: "#10B981" },
  { name: "Gemini", sub: "Google", color: "#4285F4" },
];

const features = [
  { icon: Terminal, title: "AI Kod Generasiyası", desc: "Fikrini yaz — sistem tam işlək sayt kodunu yaratsın." },
  { icon: Eye, title: "Canlı Preview", desc: "Yaradılan sayt dərhal brauzerdə real vaxtda göstərilir." },
  { icon: Boxes, title: "Full-Stack + Backend", desc: "Statik saytlar və ya PHP + MySQL backend — özün seç." },
  { icon: Zap, title: "İterativ Redaktə", desc: "AI ilə danış, kodu dəyişdir, səhvləri düzəlt." },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-[#222] bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="logo">
            <div className="w-7 h-7 bg-cyan-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter">FORGE<span className="text-cyan-500">.AI</span></span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button className="rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-medium">
                  Panel <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="nav-login">Giriş</Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-medium">
                    Başla <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1510519138101-570d1dca3d66?q=80&w=1920')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <div className="inline-flex items-center gap-2 border border-[#333] px-3 py-1 mb-8">
              <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">AI Sayt & Web Tətbiq Builder</span>
            </div>
            <h1 className="font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Fikrini yaz.<br />
              <span className="text-cyan-500">AI</span> qursun.
            </h1>
            <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
              İstədiyin AI ilə — Claude, OpenAI və ya Gemini — həm frontend, həm də backend (PHP + MySQL) tam işlək layihə yarat, kodu dəyişdir və canlı preview-da anında gör.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/dashboard" data-testid="hero-cta">
                <Button className="rounded-sm h-12 px-8 bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-semibold text-base">
                  İndi Başla <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <div className="flex items-center gap-4">
                {providers.map((p) => (
                  <div key={p.name} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="font-mono text-xs text-zinc-500">{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#222]">
          {features.map((f, i) => (
            <div key={i} className="border-r border-b border-[#222] p-8 hover:bg-[#0a0a0a] transition-colors group" data-testid={`feature-${i}`}>
              <f.icon className="w-6 h-6 text-cyan-500 mb-6 group-hover:-translate-y-[2px] transition-transform" />
              <h3 className="text-base font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="border border-[#222] bg-[#0a0a0a] p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Növbəti layihəni yarat</h2>
            <p className="mt-3 text-zinc-400">Bir cümlə yaz, dəqiqələr içində canlı sayt al.</p>
          </div>
          <Link to="/dashboard">
            <Button className="rounded-sm h-12 px-8 bg-white text-black hover:bg-cyan-500 transition-colors font-semibold">
              Panelə keç <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#222] py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <span className="font-mono text-xs text-zinc-600">FORGE.AI — built with Emergent</span>
          <div className="flex items-center gap-1 text-zinc-600">
            <GitBranch className="w-3.5 h-3.5" />
            <span className="font-mono text-xs">v1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
